import type { MPAInterceptedResponse } from '@repo/api-gw-sdk';
import { MpaApi, MPASubmitAction } from '@repo/api-gw-sdk';
import type { RefObject } from 'react';

import { createConfig } from '@/contexts/api-config';
import type { DialogStore } from '@/contexts/workspaceContext';
import { MpaCancelledError } from '@/data/dal/mpaIntercept';

import { MpaInterceptDialogContent } from './mpaInterceptDialogContent';

const MPA_INTERCEPT_DIALOG_ID = 'mpa-intercept';

interface Deps {
  baseUrl: string;
  authToken: string | undefined;
  openDialog: DialogStore['open'];
  backDialog: DialogStore['back'];
  // Single-purpose ref tracking whether our dialog is currently up. Used to
  // short-circuit concurrent intercepts so they don't queue up.
  openRef: RefObject<boolean>;
}

export const createMpaInterceptHandler = ({
  baseUrl,
  authToken,
  openDialog,
  backDialog,
  openRef,
}: Deps) => {
  // Construct an SDK client against a vanilla auth-only config so the submit
  // endpoint isn't itself subject to the MPA middleware (which would loop on
  // an accidental 201).
  const mpaApi = new MpaApi(createConfig(baseUrl, authToken));

  return (payload: MPAInterceptedResponse): Promise<void> =>
    new Promise<void>((resolve, reject) => {
      if (openRef.current) {
        reject(new MpaCancelledError());
        return;
      }
      openRef.current = true;

      let settled = false;
      const finish = (after: () => void) => {
        if (settled) return;
        settled = true;
        openRef.current = false;
        backDialog();
        after();
      };

      const submit = async (action: MPASubmitAction, comment?: string) => {
        if (settled) return;
        const projectId = payload.mpaPolicies?.policies[0]?.projectId;
        const requestId = payload.mpaRequest?.id;
        if (!projectId || !requestId) {
          throw new Error('MPA payload is missing projectId or requestId');
        }
        await mpaApi.submitMyMPARequest(projectId, requestId, {
          action,
          comment: comment?.trim() || undefined,
        });
      };

      const approve = async (comment: string) => {
        await submit(MPASubmitAction.MPA_SUBMIT_ACTION_CONFIRM, comment);
        finish(resolve);
      };

      const cancel = async () => {
        await submit(MPASubmitAction.MPA_SUBMIT_ACTION_DISCARD);
        finish(() => reject(new MpaCancelledError()));
      };

      // The header X has no UI to display a discard failure, so we fire the
      // request best-effort and always finish synchronously. Otherwise an
      // error would be swallowed, leaving openRef stuck true (blocking future
      // intercepts) and the SDK call hung. The CREATED MPA request will time
      // out server-side if the discard never lands.
      const abandon = () => {
        void submit(MPASubmitAction.MPA_SUBMIT_ACTION_DISCARD).catch(() => {});
        finish(() => reject(new MpaCancelledError()));
      };

      openDialog(
        MPA_INTERCEPT_DIALOG_ID,
        <MpaInterceptDialogContent
          payload={payload}
          onApprove={approve}
          onCancel={cancel}
        />,
        {
          header: {
            title: '',
            hideBorder: true,
            onClose: abandon,
          },
          isLoading: false,
          disableGutters: true,
          sx: {
            width: '840px',
            maxWidth: '90vw',
            height: '90vh',
            '[data-testid="panel-body"] > div': { py: '0 !important' },
          },
        }
      );
    });
};
